import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { BankAccountResponse } from "@/models/api/administration/exportConfiguration/bankAccount";
import { BankAccountExportConfiguration } from "@/models/api/administration/exportConfiguration/bankAccountExportConfiguration";
import { Integration } from "@/models/api/administration/exportConfiguration/integration";
import {
  ServiceTypeResponse,
  TranslateToServiceType,
} from "@/models/api/administration/exportConfiguration/serviceType";
import { DocumentTypeRegisterResponse } from "@/models/api/administration/exportConfiguration/documentTypeRegister";
import { SelectItem } from "@/models/api/SelectItem";
import { EditDocumentCatagory, EditRegister } from "@/models/api/administration/exportConfiguration/editRegister";

//#region Routes
export const URL_GET_BANK_ACCOUNT_LIST = "/ExportConfiguration/GetBankAccountList/Paged";
export const URL_GET_BANK_ACCOUNT_EXPORT_CONFIGURATION =
  "/ExportConfiguration/GetBankAccountExportConfiguration/${BankAccountId}";
export const URL_POST_SAVE_BANK_ACCOUNT_EXPORT_CONFIGURATION =
  "/ExportConfiguration/SaveBankAccountExportConfiguration/${BankAccountId}";
export const URL_POST_DELETE_BANK_ACCOUNT_EXPORT_CONFIGURATION =
  "/ExportConfiguration/DeleteBankAccountExportConfiguration/${BankAccountId}";
export const URL_GET_INTEGRATION_LIST = "/ExportConfiguration/GetIntegrationList";
export const URL_GET_SERVICE_TYPE_LIST = "/ExportConfiguration/GetServiceTypeList/${IntegrationId}/Paged";
export const URL_GET_PROGRAM_LIST = "/ExportConfiguration/ProgramList";
export const URL_GET_REGISTER_LIST = "/ExportConfiguration/RegisterList/${ProgramId}";
export const URL_POST_EDIT_REGISTER = "/ExportConfiguration/EditRegister";
export const URL_POST_EDIT_DOCUMENT_CATEGORY = "/ExportConfiguration/EditDocumentCategory";
export const URL_POST_ADD_NEW_SERVICE_TYPE_TRANSLATION =
  "/ExportConfiguration/${IntegrationId}/ServiceType/${ServiceTypeId}/Add";
export const URL_POST_REMOVE_SERVICE_TYPE_TRANSLATION =
  "/ExportConfiguration/ServiceTypeTranslation/${ServiceTypeTranslationId}/Remove";
export const URL_POST_EDIT_SERVICE_TYPE_TRANSLATION =
  "/ExportConfiguration/ServiceTypeTranslation/${ServiceTypeTranslationId}/Edit";

//#endregion

const _getBankAccountList = new CancellableHelper<BankAccountResponse>(URL_GET_BANK_ACCOUNT_LIST, BankAccountResponse);
_getBankAccountList.jsonSerializer = true;
export const getBankAccountList = async (genericTableRequest: GenericTableRequest): Promise<BankAccountResponse> => {
  return await _getBankAccountList.get(genericTableRequest);
};

const _getBankAccountExportConfiguration = new CancellableHelper<BankAccountExportConfiguration | null>(
  URL_GET_BANK_ACCOUNT_EXPORT_CONFIGURATION,
  BankAccountExportConfiguration,
  true
);
export const getBankAccountExportConfiguration = async (
  bankAccountId: number
): Promise<BankAccountExportConfiguration | null> => {
  return await _getBankAccountExportConfiguration.get({
    BankAccountId: bankAccountId,
  });
};

const _postSaveBankAccountExportConfiguration = new CancellableHelper<void>(
  URL_POST_SAVE_BANK_ACCOUNT_EXPORT_CONFIGURATION,
  null,
  true
);
export const postSaveBankAccountExportConfiguration = async (
  bankAccountId: number,
  exportConfiguration: BankAccountExportConfiguration
): Promise<void> => {
  await _postSaveBankAccountExportConfiguration.post({ BankAccountId: bankAccountId }, exportConfiguration);
};

const _postDeleteBankAccountExportConfiguration = new CancellableHelper<void>(
  URL_POST_DELETE_BANK_ACCOUNT_EXPORT_CONFIGURATION,
  null,
  true
);
export const postDeleteBankAccountExportConfiguration = async (bankAccountId: number): Promise<void> => {
  await _postDeleteBankAccountExportConfiguration.post({ BankAccountId: bankAccountId }, null);
};

const _getIntegrationList = new CancellableHelper<Array<Integration>>(URL_GET_INTEGRATION_LIST, Integration);
export const getIntegrationList = async (): Promise<Array<Integration>> => {
  return await _getIntegrationList.get();
};

const _getServiceTypeList = new CancellableHelper<ServiceTypeResponse>(URL_GET_SERVICE_TYPE_LIST, ServiceTypeResponse);
_getServiceTypeList.jsonSerializer = true;
export const getServiceTypeList = async (
  integrationId: string,
  genericTableRequest: GenericTableRequest
): Promise<ServiceTypeResponse> => {
  return await _getServiceTypeList.get({
    IntegrationId: integrationId,
    ...genericTableRequest,
  });
};

const _getProgramList = new CancellableHelper<Array<SelectItem>>(URL_GET_PROGRAM_LIST, SelectItem);
export const getProgramList = async (): Promise<Array<SelectItem>> => {
  return await _getProgramList.get();
};

const _getRegisterList = new CancellableHelper<DocumentTypeRegisterResponse>(
  URL_GET_REGISTER_LIST,
  DocumentTypeRegisterResponse
);
_getRegisterList.jsonSerializer = true;
export const getRegisterList = async (
  programId: number,
  genericTableRequest: GenericTableRequest
): Promise<DocumentTypeRegisterResponse> => {
  return await _getRegisterList.get({
    ProgramId: programId,
    ...genericTableRequest,
  });
};

const _postEditRegister = new CancellableHelper<void>(URL_POST_EDIT_REGISTER, null, true);
export const postEditRegister = async (model: EditRegister): Promise<void> => {
  await _postEditRegister.post(null, model);
};

const _postEditDocumentCategory = new CancellableHelper<void>(URL_POST_EDIT_DOCUMENT_CATEGORY, null, true);
export const postEditDocumentCategory = async (model: EditDocumentCatagory): Promise<void> => {
  await _postEditDocumentCategory.post(null, model);
};

const _addNewServiceTypeTranslation = new CancellableHelper<number>(URL_POST_ADD_NEW_SERVICE_TYPE_TRANSLATION);
export const addNewServiceTypeTranslation = async (
  integrationId: string,
  serviceTypeId: number,
  translateServiceType: TranslateToServiceType
): Promise<number> => {
  return await _addNewServiceTypeTranslation.post(
    { ServiceTypeId: serviceTypeId, IntegrationId: integrationId },
    translateServiceType
  );
};

const _removeServiceTypeTranslation = new CancellableHelper<void>(URL_POST_REMOVE_SERVICE_TYPE_TRANSLATION, null, true);
export const removeServiceTypeTranslation = async (serviceTypeTranslationId: number): Promise<void> => {
  await _removeServiceTypeTranslation.post({ ServiceTypeTranslationId: serviceTypeTranslationId }, null);
};

const _editServiceTypeTranslation = new CancellableHelper<void>(URL_POST_EDIT_SERVICE_TYPE_TRANSLATION, null, true);
export const editServiceTypeTranslation = async (
  serviceTypeTranslationId: number,
  editServiceTypeTranslation: TranslateToServiceType
): Promise<void> => {
  await _editServiceTypeTranslation.post(
    { ServiceTypeTranslationId: serviceTypeTranslationId },
    editServiceTypeTranslation
  );
};
